import { dedupeBlocks } from './crossCover';
import { idOf, samePerson } from './people';
import { sortLocations } from './locationOrder';
import { onLeave } from './leave';

// Source the skill list from the central catalogue (global_skills) UNIONED
// with skills actually in use on employees and locations -- not from
// employee assignments alone. Deriving it only from emp.skills meant a
// newly created skill nobody had been assigned yet never appeared in the
// qualifications selector (visible only after first assignment). Shared by
// App.js and OperationsTab.js so the two skill pickers can't silently drift.
export const getAllSkills = (schedule) => Array.from(new Set([
  ...(schedule?.globalSkills || schedule?.global_skills || []),
  ...(schedule?.employees || []).flatMap(emp => emp.skills || []),
  ...Object.values(schedule?.locationSkills || schedule?.location_skills || {}).flat(),
])).sort();

export const getEmployeeStats = (schedule) => {
  if (!schedule || !schedule.employees || !schedule.shifts) return [];
  const stats = {};
  
  schedule.employees.forEach(emp => {
    stats[emp.id] = {
      name: emp.name,
      totalShifts: 0,
      morningShifts: 0,
      afternoonShifts: 0,
      otShifts: 0,
      clinicShifts: 0,
      locations: new Set()
    };
  });

  // Count WORK BLOCKS: a cross-cover (two wards, same hours) is one shift. The
  // representative item carries every ward in crossCoverLocations so the
  // location set still lists both.
  dedupeBlocks(schedule.shifts.filter(s => s.employee)).forEach(shift => {
    const empName = idOf(shift.employee);       // keyed by staff id
    if (stats[empName]) {
      stats[empName].totalShifts++;
      const hour = new Date(shift.start).getHours();
      if (hour < 12) stats[empName].morningShifts++;
      else stats[empName].afternoonShifts++;

      // The clinic's own type, never text in its name (Oct 2026).
      const isOT = (shift.locationType || shift.location_type) === 'OT';
      if (isOT) stats[empName].otShifts++;
      else stats[empName].clinicShifts++;

      (shift.crossCoverLocations || [shift.location]).forEach(l => stats[empName].locations.add(l));
    }
  });

  return Object.values(stats).sort((a, b) => b.totalShifts - a.totalShifts);
};

export const getLocationCoverage = (schedule) => {
  if (!schedule || !schedule.shifts) return [];
  
  // schedule.locations is already the user-defined order; any location seen
  // only on shifts is appended (in that same comparator, alphabetically last).
  const uniqueLocations = schedule.locations?.length > 0
    ? [...new Set([...schedule.locations, ...schedule.shifts.map(s => s.location)])]
    : [...new Set(schedule.shifts.map(s => s.location))];
  const locationOrder = sortLocations(uniqueLocations, schedule);
  
  const coverage = {};
  locationOrder.forEach(loc => {
    coverage[loc] = schedule.shifts.filter(s => s.location === loc && s.employee).length;
  });

  return locationOrder.map(location => ({
    location,
    count: coverage[location] || 0,
    percentage: schedule.shifts.length > 0 ? Math.round((coverage[location] / schedule.shifts.length) * 100) : 0
  }));
};

// `person` is the employee (or a shift's holder); they are found by staff id.
export const isEmployeeUnavailable = (schedule, person, date, timeBlock) => {
  if (!schedule || !schedule.employees || !idOf(person)) return false;
  const emp = schedule.employees.find(e => samePerson(e, person));
  if (!emp) return false;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return onLeave(emp, `${year}-${month}-${day}`, timeBlock);
};

export const getAvailableEmployeesForDay = (schedule, date) => {
  if (!schedule || !schedule.employees) return { amCount: 0, pmCount: 0 };
  
  const employees = schedule.employees;
  
  const availableAM = employees.filter(emp => {
      return !isEmployeeUnavailable(schedule, emp, date, 'AM');
  }).length;
  
  const availablePM = employees.filter(emp => {
      return !isEmployeeUnavailable(schedule, emp, date, 'PM');
  }).length;
  
  return { amCount: availableAM, pmCount: availablePM };
};
